"use client";

import { Form, Formik, type FormikHelpers } from "formik";
import { useState } from "react";

import kebabToCamelCase from "@shared/functions/kebabToCamelCase";

import postCCPAForm from "@server/front-end-api/postCCPAForm";
import postEvent from "@server/front-end-api/postEvent";

import ValidationError from "@client/components/ccpa-form/components/validation-error";
import Checkbox from "@client/components/formik/checkbox";
import Field from "@client/components/formik/field";
import Input from "@client/components/formik/input";
import PhoneNumber from "@client/components/formik/phone-number";
import Select from "@client/components/formik/select";
import ZipCode from "@client/components/formik/zip-code";

import { FormButton } from "@ui/js/components/buttons/form-button";

import type TCCPAForm from "@packages/types/ccpa-form";
import { initialValues, validationSchema } from "@packages/types/yup/ccpa-form";

// Input IDs and Labels constants
const INPUT_ID_AND_LABEL_HTMLFOR_CCPA_REQUESTOR_TYPE = "ccpa-requestor-type";
const INPUT_ID_AND_LABEL_HTMLFOR_FIRST_NAME = "first-name";
const INPUT_ID_AND_LABEL_HTMLFOR_LAST_NAME = "last-name";
const INPUT_ID_AND_LABEL_HTMLFOR_STREET_ADDRESS = "street-address";
const INPUT_ID_AND_LABEL_HTMLFOR_CITY = "city";
const INPUT_ID_AND_LABEL_HTMLFOR_STATE = "state";
const INPUT_ID_AND_LABEL_HTMLFOR_ZIP_CODE = "zip-code";
const INPUT_ID_AND_LABEL_HTMLFOR_EMAIL = "email";
const INPUT_ID_AND_LABEL_HTMLFOR_PHONE_NUMBER = "phone-number";

interface CustomDocument extends Document {
  ccpa: HTMLFormElement;
}

export default function CCPAForm() {
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (
    values: TCCPAForm.Values,
    formikHelpers: FormikHelpers<TCCPAForm.Values>,
  ) => {
    try {
      // handle form submission
      postCCPAForm(values);

      setSuccess(true);

      formikHelpers.resetForm();

      // manually reset checkboxes
      (document as CustomDocument).ccpa
        .querySelectorAll('input[type="checkbox"]:checked')
        .forEach(($input) =>
          $input.parentElement?.querySelector("label")?.click(),
        );
    } catch (e) {
      postEvent("error", {
        error: {
          error_message: `An error occurred while attempting to submit the CCPA form.`,
          error_object: "CCPA Form",
          error_type: "Front-End API",
          path: window.location.href,
        },
      });

      formikHelpers.setStatus("error");
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={validationSchema}
    >
      <Form
        className="form"
        name="ccpa"
        noValidate={true}
        data-spec-ccpa-form
        method="POST"
        action=""
      >
        <Field
          id={INPUT_ID_AND_LABEL_HTMLFOR_CCPA_REQUESTOR_TYPE}
          name={kebabToCamelCase(
            INPUT_ID_AND_LABEL_HTMLFOR_CCPA_REQUESTOR_TYPE,
          )}
          label="The Requestor is a"
        >
          <Select
            id={INPUT_ID_AND_LABEL_HTMLFOR_CCPA_REQUESTOR_TYPE}
            name={kebabToCamelCase(
              INPUT_ID_AND_LABEL_HTMLFOR_CCPA_REQUESTOR_TYPE,
            )}
          >
            <option value="Non-California Resident">
              Non-California Resident
            </option>
            <option value="California Resident">California Resident</option>
            <option value="Duly authorized third-party representative of a California Resident">
              Duly authorized third-party representative of a California
              Resident
            </option>
          </Select>
        </Field>
        <Field
          name="ccpaRequestTypeIds"
          label="I would like to submit the following requests:"
        >
          <Input type="hidden" name="ccpaRequestTypeIds" required />
          <Checkbox
            id="ccpa-request-type-ids-1"
            label="What data is collected about me"
            name="ccpaRequestTypeIds"
            required={false}
            value="1"
          />
          <Checkbox
            id="ccpa-request-type-ids-2"
            label="Who has received my data"
            name="ccpaRequestTypeIds"
            required={false}
            value="2"
          />
          <Checkbox
            id="ccpa-request-type-ids-3"
            label="Provide a portable version of my data"
            name="ccpaRequestTypeIds"
            required={false}
            value="3"
          />
          <Checkbox
            id="ccpa-request-type-ids-4"
            label="Delete my data"
            name="ccpaRequestTypeIds"
            required={false}
            value="4"
          />
          <Checkbox
            id="ccpa-request-type-ids-5"
            label="Do not sell my data"
            name="ccpaRequestTypeIds"
            required={false}
            value="5"
          />
        </Field>
        <div className="gap-4 grid grid-cols-1 md:grid-cols-2">
          <Field
            id={INPUT_ID_AND_LABEL_HTMLFOR_FIRST_NAME}
            name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_FIRST_NAME)}
            label="First name"
          >
            <Input
              id={INPUT_ID_AND_LABEL_HTMLFOR_FIRST_NAME}
              name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_FIRST_NAME)}
            />
          </Field>
          <Field
            id={INPUT_ID_AND_LABEL_HTMLFOR_LAST_NAME}
            name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_LAST_NAME)}
            label="Last name"
          >
            <Input
              id={INPUT_ID_AND_LABEL_HTMLFOR_LAST_NAME}
              name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_LAST_NAME)}
            />
          </Field>
          <Field
            id={INPUT_ID_AND_LABEL_HTMLFOR_STREET_ADDRESS}
            name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_STREET_ADDRESS)}
            label="Street Address"
          >
            <Input
              id={INPUT_ID_AND_LABEL_HTMLFOR_STREET_ADDRESS}
              name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_STREET_ADDRESS)}
              placeholder="3170 N Sheridan"
            />
          </Field>
          <Field
            id={INPUT_ID_AND_LABEL_HTMLFOR_CITY}
            name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_CITY)}
            label="City"
          >
            <Input
              id={INPUT_ID_AND_LABEL_HTMLFOR_CITY}
              name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_CITY)}
              placeholder="Chicago"
            />
          </Field>
          <Field
            id={INPUT_ID_AND_LABEL_HTMLFOR_STATE}
            name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_STATE)}
            label="State"
          >
            <Select
              id={INPUT_ID_AND_LABEL_HTMLFOR_STATE}
              name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_STATE)}
            >
              <option value="" disabled={true}>
                Select a state
              </option>
              <option value="AL">Alabama</option>
              <option value="AK">Alaska</option>
              <option value="AZ">Arizona</option>
              <option value="AR">Arkansas</option>
              <option value="CA">California</option>
              <option value="CO">Colorado</option>
              <option value="CT">Connecticut</option>
              <option value="DE">Delaware</option>
              <option value="DC">District Of Columbia</option>
              <option value="FL">Florida</option>
              <option value="GA">Georgia</option>
              <option value="HI">Hawaii</option>
              <option value="ID">Idaho</option>
              <option value="IL">Illinois</option>
              <option value="IN">Indiana</option>
              <option value="IA">Iowa</option>
              <option value="KS">Kansas</option>
              <option value="KY">Kentucky</option>
              <option value="LA">Louisiana</option>
              <option value="ME">Maine</option>
              <option value="MD">Maryland</option>
              <option value="MA">Massachusetts</option>
              <option value="MI">Michigan</option>
              <option value="MN">Minnesota</option>
              <option value="MS">Mississippi</option>
              <option value="MO">Missouri</option>
              <option value="MT">Montana</option>
              <option value="NE">Nebraska</option>
              <option value="NV">Nevada</option>
              <option value="NH">New Hampshire</option>
              <option value="NJ">New Jersey</option>
              <option value="NM">New Mexico</option>
              <option value="NY">New York</option>
              <option value="NC">North Carolina</option>
              <option value="ND">North Dakota</option>
              <option value="OH">Ohio</option>
              <option value="OK">Oklahoma</option>
              <option value="OR">Oregon</option>
              <option value="PA">Pennsylvania</option>
              <option value="RI">Rhode Island</option>
              <option value="SC">South Carolina</option>
              <option value="SD">South Dakota</option>
              <option value="TN">Tennessee</option>
              <option value="TX">Texas</option>
              <option value="UT">Utah</option>
              <option value="VT">Vermont</option>
              <option value="VA">Virginia</option>
              <option value="WA">Washington</option>
              <option value="WV">West Virginia</option>
              <option value="WI">Wisconsin</option>
              <option value="WY">Wyoming</option>
            </Select>
          </Field>
          <Field
            id={INPUT_ID_AND_LABEL_HTMLFOR_ZIP_CODE}
            name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_ZIP_CODE)}
            label="Zip"
          >
            <ZipCode
              id={INPUT_ID_AND_LABEL_HTMLFOR_ZIP_CODE}
              name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_ZIP_CODE)}
              placeholder="60657"
            />
          </Field>
          <Field
            id={INPUT_ID_AND_LABEL_HTMLFOR_EMAIL}
            name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_EMAIL)}
            label="Email address"
          >
            <Input
              id={INPUT_ID_AND_LABEL_HTMLFOR_EMAIL}
              type="email"
              name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_EMAIL)}
              placeholder="name@example.com"
            />
          </Field>
          <Field
            id={INPUT_ID_AND_LABEL_HTMLFOR_PHONE_NUMBER}
            name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_PHONE_NUMBER)}
            label="Phone"
          >
            <PhoneNumber
              id={INPUT_ID_AND_LABEL_HTMLFOR_PHONE_NUMBER}
              name={kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR_PHONE_NUMBER)}
            />
          </Field>
        </div>
        <FormButton text="Submit" />
        <ValidationError />
        {success && (
          <div
            className="bg-green-50 rounded-lg p-4 text-green-900"
            data-spec="ccpa-form-success"
          >
            We have received your request.
          </div>
        )}
      </Form>
    </Formik>
  );
}
